import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Briefcase, Clock, ArrowUpRight, Search } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import HeroBanner from '../components/HeroBanner';
import { listJobs } from '../lib/api';

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [industry, setIndustry] = useState('All');

  useEffect(() => {
    window.scrollTo(0, 0);
    listJobs().then(setJobs).catch(() => setJobs([])).finally(() => setLoading(false));
  }, []);

  const industries = ['All', ...Array.from(new Set(jobs.map((j) => j.industry)))];
  const filtered = jobs.filter((j) => {
    if (industry !== 'All' && j.industry !== industry) return false;
    if (q) {
      const hay = `${j.title} ${j.skills.join(' ')} ${j.location}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="bg-[#FAF7F5]">
      <HeroBanner
        image="/hero/careers.jpg"
        overlay="default"
        height="min-h-[460px] lg:min-h-[540px]"
      >
        <div className="pt-[120px] lg:pt-[140px] pb-12">
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-end erbity-fade-up">
              <div className="lg:col-span-8">
                <EyebrowLabel>Careers at Erbity clients</EyebrowLabel>
                <SectionTitle as="h1" className="mt-6 text-[44px] lg:text-[68px] leading-[1.04]">
                  Engineering roles that <span className="italic text-[#7E2E4A]">move</span> silicon.
                </SectionTitle>
              </div>
              <div className="lg:col-span-4 erbity-fade-up-1">
                <p className="text-[16px] text-[#3B333A] leading-relaxed">
                  Curated open roles across semiconductor, automotive, aerospace and industrial
                  engineering &mdash; sourced by engineers, screened before you apply.
                </p>
              </div>
            </div>
          </Container>
        </div>
      </HeroBanner>

      <section className="pb-16 lg:pb-20">
        <Container>
          <div className="bg-white rounded-2xl border border-[#EADDE2] p-4 mb-8 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A6A72]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by role, skill or location"
                className="w-full pl-11 pr-4 py-3 rounded-xl bg-[#FAF7F5] border border-transparent focus:border-[#C4506F] outline-none text-[14.5px]"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {industries.map((i) => (
                <button
                  key={i}
                  onClick={() => setIndustry(i)}
                  className={`px-3.5 py-2 rounded-full text-[12.5px] border transition-colors ${
                    industry === i
                      ? 'bg-[#1A1420] text-white border-[#1A1420]'
                      : 'bg-white text-[#4E434A] border-[#EADDE2] hover:border-[#C4506F]'
                  }`}
                >
                  {i}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-[#7A6A72] text-[14px]">Loading roles&hellip;</div>
          ) : filtered.length === 0 ? (
            <div className="text-[#7A6A72] text-[14px] py-16 text-center">No open roles match your filters.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              {filtered.map((j) => (
                <Link
                  key={j.id}
                  to={`/careers/${j.id}`}
                  className="group bg-white rounded-2xl border border-[#EADDE2] p-7 hover:border-[#C4506F] transition-colors flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">{j.industry}</div>
                      <h3 className="mt-3 font-serif text-[24px] leading-tight text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{j.title}</h3>
                    </div>
                    <ArrowUpRight size={18} className="text-[#1A1420] group-hover:rotate-45 transition-transform duration-300" />
                  </div>
                  <div className="mt-5 flex flex-wrap items-center gap-4 text-[13px] text-[#4E434A]">
                    <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-[#7E2E4A]" /> {j.location}</span>
                    <span className="inline-flex items-center gap-1.5"><Briefcase size={13} className="text-[#7E2E4A]" /> {j.type}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock size={13} className="text-[#7E2E4A]" /> {j.experience}</span>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {j.skills.slice(0, 5).map((s) => (
                      <span key={s} className="text-[11.5px] px-2.5 py-1 rounded-full bg-[#FAF7F5] border border-[#EADDE2] text-[#4E434A]">{s}</span>
                    ))}
                  </div>
                  <p className="mt-5 pt-5 border-t border-[#EADDE2] text-[13.5px] text-[#4E434A] leading-relaxed line-clamp-3">{j.description}</p>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-16 rounded-3xl border border-[#EADDE2] bg-white p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <SectionTitle className="text-[24px] lg:text-[32px] leading-tight">Don&rsquo;t see the right role?</SectionTitle>
              <p className="mt-3 text-[14.5px] text-[#4E434A] max-w-lg">Send us your CV and we&rsquo;ll match you against unlisted, confidential mandates in our network.</p>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1A1420] text-white text-[14.5px] font-medium pl-5 pr-2 py-2 rounded-full hover:bg-[#3D1A2A] transition-colors">
              <span className="py-1">Submit your CV</span>
              <span className="w-9 h-9 rounded-full bg-[#C4506F] flex items-center justify-center"><ArrowUpRight size={15} /></span>
            </Link>
          </div>
        </Container>
      </section>
    </div>
  );
}
